import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CordovaService } from 'src/app/core/cordova.service';
import { AiSistantDisplayModeEnum } from 'src/app/parameters/alcmeon-ai/shared/enums';
import { EmojiPickerModalComponent } from 'src/app/shared/components/emojis/emoji-picker-modal.component';
import { TextareaAutoExpandDirective } from 'src/app/shared/directives/textarea-auto-expand.directive';
import { BasicService } from 'src/app/shared/services/basic.service';
import { KeyboardShowHideService } from 'src/app/shared/services/keyboard-show-hide.service';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { TaskProcessUpdatesService } from 'src/app/task-process/shared/task-process-updates.service';
import { TaskProcessService } from 'src/app/task-process/shared/task-process.service';
import { environment } from 'src/environments/environment';

import { ActionButtonConfig } from '../../action-button-config';
import { EditorConfig } from '../../editor-config';
import { UserMentionPlaceholderEnum } from '../../enums';
import { Favorite } from '../../models';
import { AttachmentType } from '../../models/misc.model';
import { SuggestAnswerService } from '../../services/suggest-answer.service';
import { FavoritesModalComponent } from '../favorites-modal/favorites-modal.component';
import { CodeMirrorWrapperComponent } from './code-mirror-wrapper/codemirror-wrapper.component';
import { CodemirrorContext } from './codemirror-context';
import { CodemirrorSpellcheckerComponent } from './codemirror-spellchecker/codemirror-spellchecker.component';
import { SingleMessage } from './single-message';

@Component({
  selector: 'app-single-message-editor',
  templateUrl: './single-message-editor.component.html',
  styleUrls: ['./single-message-editor.component.scss'],
})
export class SingleMessageEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() editorConfig: EditorConfig;
  @Input() message: SingleMessage;
  @Input() favorites: Favorite[];
  @Input() favoritesAllLang: Favorite[];
  @Input() isPublish = false;
  @Input() isReplyingInPrivate: boolean;
  @Input() scrollContainer: HTMLElement;
  @Input() siteId: number;
  @Input() taskProcessContext: TaskProcessContext;

  @ViewChild(TextareaAutoExpandDirective) textareaDirective: TextareaAutoExpandDirective;
  @ViewChild(CodeMirrorWrapperComponent) codeMirrorWrapper: CodeMirrorWrapperComponent;
  @ViewChild(CodemirrorSpellcheckerComponent) codeMirrorSpellchecker: CodemirrorSpellcheckerComponent;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  AttachmentType = AttachmentType;

  codedmirrorContext: CodemirrorContext;
  userPlaceholderAndFavoritesAndAttachmentButtons$: Observable<ActionButtonConfig[]>;

  taskId = this.taskProcessUpdatesService.getProcessedTaskId();

  private ngOnDestroy$ = new Subject();
  private subscriptions = new Subscription();

  constructor(
    private cordovaService: CordovaService,
    private keyboardShowHideService: KeyboardShowHideService,
    private suggestAnswerService: SuggestAnswerService,
    private taskProcessService: TaskProcessService,
    private taskProcessUpdatesService: TaskProcessUpdatesService,
    public basicService: BasicService
  ) {}

  ngOnInit() {
    this.codedmirrorContext = new CodemirrorContext(this.editorConfig, this.siteId, this.message);
    this.userPlaceholderAndFavoritesAndAttachmentButtons$ = this.message.attachments.buttons$.pipe(
      map(attachmentButtons => [
        ...this.buttonsConfigBeforeAttachments(),
        ...attachmentButtons,
        ...this.buttonsConfigAfterAttachments(),
      ])
    );

    if (!environment.isNativeApp) {
      this.subscriptions.add(
        // Depends on the AI.ssistant setup by team, trigger the AI when the editor is displayed.
        this.suggestAnswerService.AissitantConfigForTeamBS.subscribe(config => {
          if (config.aiAgentId && config.displayMode === AiSistantDisplayModeEnum.init) {
            this.triggerAIAssistantAnswer(this.taskId);
          }
        })
      );
    }
  }

  ngAfterViewInit() {
    this.codedmirrorContext.init();
    if (this.codedmirrorContext.isSpellCheckerEnabled) {
      this.codeMirrorSpellchecker.init(this.codeMirrorWrapper);
    }
    this.message.attachments.setFileInput(this.fileInput.nativeElement);

    // When single messages are added and removed, the messages handler tells the appropriate single message editor to focus its textarea
    this.message.doFocusTextarea$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.focusEditor());
  }

  shouldDisplayAIAssistantResponse() {
    try {
      return this.basicService.answerSuggestionPending[this.taskId]?.resultBS.value;
    } catch {
      return false;
    }
  }

  removeAttachment(attachment) {
    this.message.attachments.removeAttachment(attachment);
  }

  copyAttachmentText(attachment) {
    this.message.text = attachment.value.answer;
    this.message.attachments.removeAttachment(attachment);
  }

  onFileSelected($event) {
    this.message.attachments.onFileSelected($event.target.files);
  }

  onTextareaFocused(event) {
    this.registerTextareaWithKeyboardShowHideService(event.target);
    this.message.didFocus();
  }

  onTextAreaChanged(value: string) {
    this.message.text = value;
    this.taskProcessService.handleTypingBS.next(value);
  }

  onCodeMirrorFocused() {
    this.message.didFocus();
  }

  onTyping(value) {
    this.taskProcessService.handleTypingBS.next(value);
  }

  toggleDMSwitch() {
    this.message.toggleDMSwitch();
  }

  toggleDMSwitchTooltipKey() {
    return this.message.isDMSwitchChecked
      ? 'm/task-process/tooltips/remove-link-to-dm'
      : 'm/task-process/tooltips/link-to-dm';
  }

  shouldShowLinkShortenerSwitch() {
    return this.editorConfig.isLinkShortenerEnabled && this.message.links.length > 0;
  }

  toggleLinkShortenerSwitch() {
    this.message.areLinkedShortened = !this.message.areLinkedShortened;
  }

  toggleLSSwitchTooltipKey() {
    return this.message.areLinkedShortened
      ? 'm/task-process/tooltips/deactivate-url-shortener'
      : 'm/task-process/tooltips/activate-url-shortener';
  }

  private async onUserPlaceholderBtnClicked(event) {
    const options = [
      this.placeholderPopoverOption('m/task-process/username-selector/fullname', UserMentionPlaceholderEnum.user),
    ];
    if (this.editorConfig.offersFirstNamePlaceholder) {
      options.push(
        this.placeholderPopoverOption(
          'm/task-process/username-selector/firstname',
          UserMentionPlaceholderEnum.firstName
        )
      );
    }
    if (this.editorConfig.offersFullNamePlaceholderWithArobaseMention) {
      options.push(
        this.placeholderPopoverOption('m/task-process/username-selector/@-fullname', UserMentionPlaceholderEnum.mention)
      );
    }
    const modalResult = await this.basicService.selectInPopover(event, options);
    if (modalResult.data) {
      modalResult.data.handlerFn();
    }
  }

  private placeholderPopoverOption(textKey, mentionTemplate) {
    return {
      label: this.basicService.translate(textKey),
      handlerFn: () => this.insertUserPlaceholderIntoEditor(mentionTemplate),
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async onFavoritesButtonClicked(event) {
    const modalInput = {
      favorites: this.favoritesAllLang,
      taskProcessContext: this.taskProcessContext,
    };

    const modal = await this.basicService.cardModal(FavoritesModalComponent, modalInput);
    modal.onDidDismiss().then(result => {
      if (result.data) {
        this.fillAnswerEditorWithFavorite(result.data, false);
      }
    });
  }

  private async onEmojiPickerClicked() {
    const modal = await this.basicService.cardModal(EmojiPickerModalComponent);
    const { data } = await modal.onWillDismiss();
    if (data) {
      this.insertTextAtCursorPosition(data);
    }
  }

  private registerTextareaWithKeyboardShowHideService(textarea) {
    // as <app-answer-editor> is position:relative,
    // we can't use the textarea directly to compute its position relative to the scroll container;
    // hence we use its offset parent (the <app-answer-editor> element)
    const answerEditorElement = textarea.offsetParent;

    // The "keyboard-pushes-the-editor-and-the-messages-above" effect only works if the editor is at the bottom.
    // For public messages, the editor is inline, and it is preferable to just scroll enough so that the textarea comes into view
    const elementToKeepInView = this.isReplyingInPrivate ? null : answerEditorElement;
    this.keyboardShowHideService.setScrollElementAndTextInputElement(this.scrollContainer, elementToKeepInView);
  }

  private focusEditor() {
    if (this.codedmirrorContext.isCodeMirror) {
      this.codeMirrorWrapper.doFocus();
    } else {
      this.textareaDirective.ta.focus();
    }
  }

  fillAnswerEditorWithFavorite(favorite, shouldFocusEditor: boolean) {
    this.message.text = favorite.editorModel.text || '';
    setTimeout(() => {
      // give angular a chance to update the editor value
      if (this.codedmirrorContext.isCodeMirror) {
        this.codedmirrorContext.codeMirror.execCommand('goDocEnd');
      } else {
        this.textareaDirective.updateRows();
      }
    });

    this.message.attachments.clearAttachments();
    favorite.editorModel.attachments.forEach(attachment => {
      switch (attachment.type) {
        case 'picture':
          this.message.attachments.addPictureAttachment(attachment.value.pictureId);
          break;
        case 'video':
          this.message.attachments.addVideoAttachment(attachment.value.videoId);
          break;
        case 'file':
          this.message.attachments.addFileAttachment(attachment.value.fileId, attachment.value.fileName);
          break;
        case 'bzcRichLink':
          this.message.attachments.addBzcRichLinkAttachment(attachment.value);
          break;
        default:
          console.error('Unsupported favorite type !');
      }
    });
    if (shouldFocusEditor) {
      this.message.doFocusAfterTimeout();
    }
  }

  fillAnswerEditorWithAIAssistantAnswer({ id, answer }, shouldFocusEditor: boolean) {
    this.taskProcessUpdatesService.rattachAnswerToAIAssistantSuggestedAnswer(id, answer);
    this.message.text = answer || '';
    setTimeout(() => {
      if (this.codedmirrorContext.isCodeMirror) {
        this.codedmirrorContext.codeMirror.execCommand('goDocEnd');
      } else {
        this.textareaDirective.updateRows();
      }
    });

    this.message.attachments.clearAttachments();
    if (shouldFocusEditor) {
      this.message.doFocusAfterTimeout();
    }
  }

  rejectAnswerEditorWithAIAssistantAnswer({ id, answer }) {
    this.taskProcessUpdatesService.rattachAnswerToAIAssistantSuggestedAnswer(id, answer, true);
  }

  // Inserts the selected user placeholder at the cursor position of the current message in editor
  private insertUserPlaceholderIntoEditor(placeholder: string) {
    this.insertTextAtCursorPosition(`${placeholder} `);
  }

  private insertTextAtCursorPosition(text: string) {
    if (this.codedmirrorContext.isCodeMirror) {
      this.codeMirrorWrapper.insertTextAtCursorPosition(text);
    } else {
      const start: number = this.textareaDirective.ta.selectionStart;
      const end: number = this.textareaDirective.ta.selectionEnd;
      this.message.text = this.message.text.substring(0, end) + text + this.message.text.substring(end);
      setTimeout(() => {
        this.textareaDirective.updateRows();
        this.textareaDirective.ta.selectionStart = start + text.length;
        this.textareaDirective.ta.selectionEnd = end + text.length;
      });
    }
    setTimeout(() => this.focusEditor(), 700); // Not sure why such a long timeout is necessary,
    // maybe the ActionSheet close animation is messing with the focus
  }

  private buttonsConfigBeforeAttachments() {
    const userPlaceholderButton = new ActionButtonConfig(
      'm/task-process/editor-actions/mention',
      'at-outline',
      event => {
        this.onUserPlaceholderBtnClicked(event);
      }
    );
    const favoritesButton = new ActionButtonConfig('m/task-process/editor-actions/favorite', 'book', event => {
      this.onFavoritesButtonClicked(event);
    });
    const result = this.editorConfig.offersPlaceholderButton ? [userPlaceholderButton] : [];
    result.push(favoritesButton);
    return result;
  }

  private buttonsConfigAfterAttachments() {
    const emojiButton = new ActionButtonConfig('m/task-process/editor-actions/emoji', 'happy-outline', () => {
      this.onEmojiPickerClicked();
    });
    const result = [];
    // 00001530 - some users weren't seeing the emoji button.
    // That was because Ionic uses 'any-pointer: coarse' to identify mobile devices,
    // and  hence PCs with a touchsreen weren't seeing the emoji button.
    // We tried with detecting the presence of a mouse (with any-pointer:fine)
    // but it didn't work either for some user (probably using some Firefox ESR version).
    // Let's use the OS instead
    if (!environment.isNativeApp && !this.cordovaService.hasEmojiKeyboard()) {
      // mobile devices have an emoji picker integrated with their keyboard
      result.push(emojiButton);
    }
    return result;
  }

  /**
   * Allows to call the Generative AI with the taskId. This G. AI should work with this one to gather the context and compute a suggested answer.
   * The answer will is retrieved through a WebSocket ws.ai.suggest-answer
   * @param taskId The requested task id.
   */
  private triggerAIAssistantAnswer = (taskId: number) => {
    this.suggestAnswerService.getSuggestAnswer(taskId);
  };

  ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
    this.subscriptions?.unsubscribe();
  }
}
